import { useNavigate } from 'react-router-dom';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';

export default function ConsentimientoPage() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;

  const trab = state.trabajadorActual;
  const centro = state.centrosTrabajo[state.centroActualIdx];
  const empresa = state.empresa;
  if (!trab || !centro || !empresa) { navigate('/cuestionarios'); return null; }

  const consentimientos = centro.consentimientos || {};
  const consentOk = !!consentimientos[trab.id];
  const fechaHoy = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

  const guardarConsent = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      dispatch({
        type: ACTION_TYPES.SET_CONSENTIMIENTO,
        payload: {
          centroIdx: state.centroActualIdx,
          trabajadorId: trab.id,
          data: {
            fecha: new Date().toISOString(), fechaDisplay: fechaHoy,
            nombreTrabajador: trab.nombreCompleto || trab.nombre,
            numEmpleado: trab.numEmpleado, centro: centro.nombreCentro, empresa: empresa.nombre,
            archivo: file.name, tamano: (file.size / 1024).toFixed(1) + ' KB',
            archivoData: ev.target.result.split(',')[1], archivoTipo: file.type, // base64 sin el prefijo data:mime;base64,
          },
        },
      });
    };
    reader.readAsDataURL(file);
  };

  const clausulas = [
    { num: 1, titulo: 'Propósito de la evaluación', texto: `La presente evaluación tiene como objetivo identificar y analizar los factores de riesgo psicosocial en el centro de trabajo "${centro.nombreCentro}" de la empresa "${empresa.nombre}", en cumplimiento con la NOM-035-STPS-2018.` },
    { num: 2, titulo: 'Información que se recabará', texto: 'Se recabarán datos personales generales (sexo, estado civil, nivel de estudios, puesto, antigüedad, tipo de contratación y jornada laboral) a través de la Guía V, así como respuestas a cuestionarios sobre acontecimientos traumáticos severos y factores de riesgo psicosocial.' },
    { num: 3, titulo: 'Confidencialidad', texto: 'Las respuestas serán tratadas de forma estrictamente confidencial. Los resultados se presentarán de manera agregada y en ningún caso se identificará de forma individual a los trabajadores.' },
    { num: 4, titulo: 'Uso de los resultados', texto: 'Los resultados serán utilizados exclusivamente para medidas de prevención y acciones de control conforme a la NOM-035-STPS-2018.' },
    { num: 5, titulo: 'Obligaciones de los trabajadores', subs: [
      '5.1 Observar las medidas de prevención y control que dispone esta Norma.',
      '5.2 Abstenerse de realizar prácticas contrarias al entorno organizacional favorable y actos de violencia laboral.',
      '5.3 Participar en la identificación de los factores de riesgo psicosocial.',
      '5.4 Informar sobre prácticas opuestas al entorno organizacional favorable y denunciar actos de violencia laboral.',
      '5.5 Informar por escrito al patrón haber presenciado o sufrido un acontecimiento traumático severo.',
      '5.6 Participar en los eventos de información que proporcione el patrón.',
      '5.7 Someterse a los exámenes médicos y evaluaciones psicológicas que determinan la presente Norma.',
    ]},
  ];

  const cardStyle = { padding: '10px 14px', background: 'rgba(99,102,241,0.04)', borderRadius: 10, margin: '10px 0', borderLeft: '3px solid rgba(99,102,241,0.3)' };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Consentimiento informado</h2>
      <p style={{ color: t.textMuted, fontSize: '0.88rem', marginBottom: 20 }}>NOM-035-STPS-2018 · {trab.nombreCompleto || trab.nombre}</p>

      {/* Documento */}
      <div style={{ padding: 24, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, marginBottom: 20, fontSize: '0.85rem', color: dark ? '#ccc' : '#374151', lineHeight: 1.6 }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: t.textStrong }}>CONSENTIMIENTO INFORMADO</div>
          <div style={{ fontSize: '0.78rem', color: t.textMuted }}>Evaluación de Factores de Riesgo Psicosocial — NOM-035-STPS-2018</div>
        </div>

        <div style={{ padding: 12, background: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb', borderRadius: 8, marginBottom: 14, fontSize: '0.8rem' }}>
          <div><strong>Empresa:</strong> {empresa.nombre} {empresa.rfc && `| RFC: ${empresa.rfc}`}</div>
          <div><strong>Centro:</strong> {centro.nombreCentro}</div>
          <div><strong>Trabajador(a):</strong> {trab.nombreCompleto || trab.nombre} | No. {trab.numEmpleado} | Fecha: {fechaHoy}</div>
        </div>

        <p>Yo, <strong>{trab.nombreCompleto || trab.nombre}</strong>, declaro que he sido informado(a) sobre lo siguiente:</p>

        {clausulas.map(c => (
          <div key={c.num} style={cardStyle}>
            <strong style={{ color: t.textLink, fontSize: '0.8rem' }}>{c.num}. {c.titulo}</strong>
            {c.texto && <p style={{ margin: '4px 0 0' }}>{c.texto}</p>}
            {c.subs && <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {c.subs.map((s, i) => <p key={i} style={{ margin: 0, fontSize: '0.8rem', color: t.textMuted, lineHeight: 1.5 }}>{s}</p>)}
            </div>}
          </div>
        ))}

        <div style={{ height: 1, background: t.border, margin: '16px 0' }} />
        <p style={{ fontStyle: 'italic' }}>Al firmar este documento, declaro que he leído y comprendido la información anterior y que acepto participar en la evaluación.</p>
      </div>

      {/* Upload area */}
      <div style={{
        background: consentOk ? 'rgba(16,185,129,0.05)' : (dark ? 'rgba(255,255,255,0.03)' : '#fff'),
        border: `1px solid ${consentOk ? 'rgba(16,185,129,0.2)' : t.border}`,
        borderRadius: 14, padding: 16, marginBottom: 14,
      }}>
        {!consentOk ? (
          <>
            <div style={{ fontSize: '0.85rem', color: t.textStrong, fontWeight: 600, marginBottom: 6 }}>📎 Adjuntar consentimiento firmado</div>
            <div style={{ fontSize: '0.78rem', color: t.textMuted, marginBottom: 10 }}>Sube una foto o escaneo de este documento con la firma del trabajador.</div>
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px 0', borderRadius: 12, border: `2px dashed ${t.accentBorder}`,
              background: t.accentLight, color: t.textLink, fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
            }}>
              Seleccionar archivo (PDF, JPG, PNG)
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                onChange={e => { if (e.target.files?.[0]) guardarConsent(e.target.files[0]); }} />
            </label>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>✓</div>
              <div>
                <div style={{ fontWeight: 600, color: '#10b981', fontSize: '0.85rem' }}>{consentimientos[trab.id].archivo}</div>
                <div style={{ fontSize: '0.72rem', color: t.textMuted }}>{consentimientos[trab.id].tamano} · {consentimientos[trab.id].fechaDisplay}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={() => { dispatch({ type: ACTION_TYPES.CANCELAR_CUESTIONARIO }); navigate('/cuestionarios'); }}
          style={{ flex: 1, padding: '13px 0', borderRadius: 12, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}>
          ← Volver
        </button>
        <button
          disabled={!consentOk}
          onClick={() => navigate('/ficha/' + trab.id)}
          style={{
            flex: 2, padding: '13px 0', borderRadius: 12, border: 'none',
            background: consentOk ? '#10b981' : (dark ? '#333' : '#d1d5db'),
            color: consentOk ? '#fff' : (dark ? '#666' : '#9ca3af'),
            fontWeight: 700, cursor: consentOk ? 'pointer' : 'not-allowed', fontSize: '0.88rem',
          }}>
          {consentOk ? 'Continuar a ficha de datos →' : 'Sube el documento firmado para continuar'}
        </button>
      </div>
    </div>
  );
}
